import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import orderBy from 'lodash/orderBy';

export const Index_EcoregionFragment = gql(/* GraphQL */ `
  fragment Index_EcoregionFragment on ForestryIndicators {
    ecoRegion {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type EcoregionProps = {
  className?: string;
  indicators: FragmentType<typeof Index_EcoregionFragment>;
};

export function Ecoregion({ indicators, className }: EcoregionProps) {
  const { ecoRegion } = useFragment(Index_EcoregionFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('ecoregion');

  const pieData = useMemo(() => {
    const colors = generateChartColors(ecoRegion.items.length);
    const sorted = orderBy(
      ecoRegion.items
        .filter((item) => !!item)
        .map((item) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [ecoRegion.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip className="ml-1">{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={ecoRegion.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="left" />
      </ChartCard.Body>
    </ChartCard>
  );
}
